// SourceHero — the URL-card cluster at the top of the source detail page.
//
// Exports the four components consumed by SourceDetailPage:
//   - SourceHero          full setup-phase card with identity row, inputs
//                         rail, big webhook URL, copy/test buttons, blurb,
//                         and the "first time? show me how" affordance.
//   - CompactSourceHero   single-line URL strip used in build + monitor
//                         phases when the operator no longer needs the
//                         full setup card. Saves ~280px of vertical real
//                         estate so the events stream + flow strip get to
//                         dominate the viewport.
//   - BuildPhaseCTA       prominent panel above the event stream during
//                         Phase 2. Operator just got their first events
//                         but has no triggers yet — guide them to the
//                         next decision (validate / enrich / promote).
//   - HowToSendDialog     modal-overlay form of HowToSend. Used in
//                         compact-hero mode (build + monitor phases)
//                         where the inline HowToSend slot doesn't exist.
//
// Co-located helpers (only consumed by the four exports above):
//   - HowToSend           the actual code-snippets block (curl/Node/
//                         Python/Go/HMAC tabs).
//   - SourceNameRenamePopover  inline rename of source.name from the URL hero.
//   - SlugRenamePopover        inline rename of the URL slug, with debounced
//                              availability check + slug_history listing.
//   - UrlHeroOverflow     "..." menu bundling 4–5 secondary URL-hero
//                         actions (send custom / upload / replay / copy
//                         curl / share portal link).
//
// Extracted from pages/SourceDetailPage.tsx as part of the file split
// (Apr 2026). All four pieces share enough state shape (Source +
// webhookUrl + a few callbacks) that splitting them across files just
// produces hop-laden imports without much benefit.

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { DataHubEvent, Source } from '@datahub/shared';
import {
  checkSlugAvailability,
  patchSource,
  patchSourceSlug,
  archiveSource,
} from '../../api';
import { useToast } from '../Toast';

// ── HowToSendDialog / HowToSend ────────────────────────────────────────────
// HowToSend in modal-overlay form. Used in compact-hero mode (build +
// monitor phases) where the inline HowToSend slot doesn't exist —
// click 'how →' opens a centered dialog instead.

export function HowToSendDialog({ webhookUrl, onClose }: { webhookUrl: string; onClose: () => void }): React.ReactElement {
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 60,
        background: 'oklch(0.18 0.01 250 / 0.5)',
        display: 'flex', justifyContent: 'center', alignItems: 'flex-start', paddingTop: 80,
      }}
    >
      <div onClick={e => e.stopPropagation()} style={{ width: 'min(680px, calc(100vw - 48px))' }}>
        <HowToSend webhookUrl={webhookUrl} onClose={onClose} />
      </div>
    </div>
  );
}

function HowToSend({ webhookUrl, onClose }: { webhookUrl: string; onClose: () => void }) {
  type Lang = 'curl' | 'node' | 'python' | 'go' | 'hmac';
  const [lang, setLang] = useState<Lang>('curl');

  const samplePayload = JSON.stringify({ event: 'deal.updated', id: 'd_42', value: 24500 }, null, 2);
  const samplePayloadCompact = JSON.stringify({ event: 'deal.updated', id: 'd_42', value: 24500 });
  const snippets: Record<Lang, string> = {
    curl: `# POST any JSON / CSV / XML — Data Hub figures out the format
curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -d '${samplePayloadCompact}'

# response: 202 Accepted, body: { event_id, event_key }`,
    node: `// fetch (Node 18+, Bun, Deno)
await fetch('${webhookUrl}', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify(${samplePayload}),
});

// or with axios
import axios from 'axios';
await axios.post('${webhookUrl}', ${samplePayload});`,
    python: `import requests

requests.post(
  '${webhookUrl}',
  json=${samplePayload.replace(/"/g, "'")},
)

# httpx async equivalent
# import httpx
# async with httpx.AsyncClient() as c:
#     await c.post('${webhookUrl}', json=...)`,
    go: `package main

import (
  "bytes"
  "encoding/json"
  "net/http"
)

func main() {
  payload := map[string]any{"event": "deal.updated", "id": "d_42", "value": 24500}
  body, _ := json.Marshal(payload)
  http.Post("${webhookUrl}", "application/json", bytes.NewReader(body))
}`,
    hmac: `# Sender side — compute HMAC over the raw body
SECRET="<your hmac_secret_ref from this source>"
BODY='${samplePayloadCompact}'
SIG="sha256=$(echo -n "$BODY" | openssl dgst -sha256 -hmac "$SECRET" -hex | sed 's/^.* //')"

curl -X POST '${webhookUrl}' \\
  -H 'Content-Type: application/json' \\
  -H "X-Hub-Signature-256: $SIG" \\
  -d "$BODY"

# Data Hub will verify the header against the source's hmac_secret_ref.
# Without it, hooks still ingest — HMAC is opt-in per source.`,
  };

  const langs: { id: Lang; label: string }[] = [
    { id: 'curl', label: 'curl' },
    { id: 'node', label: 'Node / fetch' },
    { id: 'python', label: 'Python' },
    { id: 'go', label: 'Go' },
    { id: 'hmac', label: 'HMAC verify' },
  ];

  return (
    <div className="how-block">
      <div className="lang-tabs">
        {langs.map(l => (
          <button
            key={l.id}
            className="lang-tab"
            data-active={lang === l.id}
            onClick={() => setLang(l.id)}
          >
            {l.label}
          </button>
        ))}
        <button
          className="lang-tab"
          style={{ marginLeft: 'auto' }}
          onClick={onClose}
          aria-label="Close code examples"
          title="Close"
        >×</button>
      </div>
      <div className="code">
        <button
          className="code-copy"
          onClick={() => navigator.clipboard.writeText(snippets[lang])}
          title="Copy snippet"
        >copy</button>
        <pre>{snippets[lang]}</pre>
      </div>
    </div>
  );
}

// ── SourceNameRenamePopover ───────────────────────────────────────────────
// Inline rename of the human-friendly source name without leaving the URL
// hero (BACKLOG.md #11). Distinct from the slug popover (URL alias).
// Blank input + click-Save clears the name, falling back to the
// auto-generated default the API returns.
function SourceNameRenamePopover({ source, onClose, onSaved }: {
  source: Source;
  onClose: () => void;
  onSaved: (updated: { name: string }) => void;
}) {
  const [draft, setDraft] = useState(source.name ?? '');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Click-outside dismiss.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const tgt = e.target as Node | null;
      const popover = document.querySelector('.source-name-popover');
      if (popover && tgt && !popover.contains(tgt)) onClose();
    }
    setTimeout(() => window.addEventListener('mousedown', onDocClick), 50);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, [onClose]);

  async function save(): Promise<void> {
    const trimmed = draft.trim();
    if (trimmed.length > 200) { setError('Max 200 chars.'); return; }
    setSubmitting(true); setError(null);
    try {
      const updated = await patchSource(source.source_id, { name: trimmed });
      onSaved({ name: updated.name });
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  return (
    <div
      className="source-name-popover"
      style={{
        position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 30,
        width: 320, padding: 14,
        background: 'var(--bg-elev)', border: '1px solid var(--line)',
        borderRadius: 8, boxShadow: '0 8px 28px oklch(0.20 0.02 250 / 0.18)',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Rename source</div>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', marginBottom: 10, lineHeight: 1.5 }}>
        Display name only — used in lists, search, and the URL hero card.
        For URL changes use the slug button on the webhook URL below.
      </div>
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter' && !submitting) void save(); }}
        placeholder="e.g. Acme — Salesforce"
        maxLength={200}
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '6px 10px', fontFamily: 'var(--ui)', fontSize: 13,
          border: `1px solid ${error ? 'var(--danger)' : 'var(--line)'}`,
          borderRadius: 4, outline: 'none',
        }}
      />
      <div style={{ marginTop: 4, fontSize: 10.5, color: 'var(--ink-3)', display: 'flex', justifyContent: 'space-between' }}>
        <span>Max 200 chars</span>
        <span style={{ fontFamily: 'var(--mono)' }}>{draft.length}/200</span>
      </div>
      {error && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--danger)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 12 }}>
        <button className="btn sm ghost" onClick={onClose} disabled={submitting}>Cancel</button>
        <button className="btn sm primary" disabled={submitting || draft.trim() === source.name} onClick={() => { void save(); }}>
          {submitting ? 'Saving…' : 'Save'}
        </button>
      </div>
    </div>
  );
}

// ── SlugRenamePopover ─────────────────────────────────────────────────────
// Anchored under .url-slug; lets the operator give the webhook URL a
// memorable name (`/hook/pipedrive-prod` instead of `/hook/src_b3766…`).
// Live-validates against the slug-availability endpoint as the user
// types (debounced 300ms). Past aliases stay resolvable via slug_history
// (visible at the bottom of the popover).
function SlugRenamePopover({ source, onClose, onSaved }: {
  source: Source;
  onClose: () => void;
  onSaved: (updated: { slug_alias: string | null; slug_history: string[] }) => void;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(source.slug_alias ?? '');
  const [check, setCheck] = useState<{ available: boolean; reason?: string; current?: boolean } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Debounced availability check.
  useEffect(() => {
    const trimmed = draft.trim().toLowerCase();
    if (!trimmed) { setCheck(null); return; }
    if (trimmed === source.slug_alias) { setCheck({ available: true, current: true }); return; }
    const t = setTimeout(() => {
      void checkSlugAvailability(source.source_id, trimmed).then(setCheck).catch(() => setCheck(null));
    }, 300);
    return () => clearTimeout(t);
  }, [draft, source.source_id, source.slug_alias]);

  const trimmed = draft.trim().toLowerCase();
  const formatOk = !!trimmed && /^[a-z0-9_-]{1,40}$/.test(trimmed);
  const canSave = formatOk && check?.available && !check.current && !submitting;

  async function save() {
    setSubmitting(true); setError(null);
    try {
      const r = await patchSourceSlug(source.source_id, trimmed);
      onSaved({ slug_alias: r.slug_alias, slug_history: r.slug_history });
      toast.show(`Slug set to ${r.slug_alias} · old links keep working.`, { kind: 'success' });
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  async function reset() {
    setSubmitting(true); setError(null);
    try {
      const r = await patchSourceSlug(source.source_id, null);
      onSaved({ slug_alias: r.slug_alias, slug_history: r.slug_history });
      toast.show('Slug cleared · short ID is canonical again.', { kind: 'success' });
    } catch (e) {
      setError((e as Error).message);
      setSubmitting(false);
    }
  }

  // Click outside to dismiss.
  useEffect(() => {
    function onDocClick(e: MouseEvent) {
      const tgt = e.target as Node | null;
      const popover = document.querySelector('.slug-popover');
      if (popover && tgt && !popover.contains(tgt)) onClose();
    }
    setTimeout(() => window.addEventListener('mousedown', onDocClick), 50);
    return () => window.removeEventListener('mousedown', onDocClick);
  }, [onClose]);

  return (
    <div
      className="slug-popover"
      style={{
        position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 30,
        width: 360, padding: 14,
        background: 'var(--bg-elev)', border: '1px solid var(--line)',
        borderRadius: 8, boxShadow: '0 8px 28px oklch(0.20 0.02 250 / 0.18)',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 6 }}>Rename URL slug</div>
      <div style={{ fontSize: 11, color: 'var(--ink-2)', marginBottom: 10, lineHeight: 1.5 }}>
        Becomes the trailing path of <code style={{ fontFamily: 'var(--mono)' }}>/hook/…</code>.
        Old aliases stay resolvable so partner integrations don't break.
      </div>
      <input
        autoFocus
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onKeyDown={e => { if (e.key === 'Escape') onClose(); if (e.key === 'Enter' && canSave) void save(); }}
        placeholder="pipedrive-prod"
        style={{
          width: '100%', boxSizing: 'border-box',
          padding: '6px 10px', fontFamily: 'var(--mono)', fontSize: 13,
          border: `1px solid ${formatOk && check?.available !== false ? 'var(--line)' : 'var(--danger)'}`,
          borderRadius: 4, outline: 'none',
        }}
      />
      <div style={{ marginTop: 4, fontSize: 10.5, fontFamily: 'var(--mono)', color: 'var(--ink-3)' }}>
        a-z 0-9 hyphen underscore · 40 chars max
      </div>
      {trimmed && !formatOk && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--danger)' }}>Invalid characters or too long.</div>
      )}
      {trimmed && formatOk && check && !check.available && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--danger)' }}>
          {check.reason === 'taken' ? 'Already in use by another source.'
            : check.reason === 'equals_source_id' ? 'Already used by another source as its id.'
            : `Unavailable (${check.reason})`}
        </div>
      )}
      {trimmed && formatOk && check?.available && !check.current && (
        <div style={{ marginTop: 4, fontSize: 11, color: 'var(--accent)' }}>Available · /h/{trimmed}</div>
      )}
      {error && <div style={{ marginTop: 6, fontSize: 11, color: 'var(--danger)' }}>{error}</div>}

      {(source.slug_history?.length ?? 0) > 0 && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px dashed var(--line)' }}>
          <div style={{ fontSize: 10.5, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--ink-3)', marginBottom: 4 }}>past aliases · still resolve</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {source.slug_history!.map(h => (
              <code key={h} style={{ fontFamily: 'var(--mono)', fontSize: 11, padding: '2px 6px', background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 3, color: 'var(--ink-2)' }}>{h}</code>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 6, justifyContent: 'space-between', marginTop: 12 }}>
        <button className="btn sm ghost" onClick={() => { void reset(); }} disabled={submitting || !source.slug_alias} title="Clear alias — fall back to the short id">use short ID</button>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn sm ghost" onClick={onClose} disabled={submitting}>Cancel</button>
          <button className="btn sm primary" disabled={!canSave} onClick={() => { void save(); }}>{submitting ? 'Saving…' : 'Save'}</button>
        </div>
      </div>
    </div>
  );
}

// ── CompactSourceHero ─────────────────────────────────────────────────────
// Single-line URL strip used in the build + monitor phases when the
// operator no longer needs the full setup card. Saves ~280px of vertical
// real estate so the events stream + flow strip get to dominate the
// viewport.
export function CompactSourceHero({ source, webhookUrl, phase, onOpenFull, onAddInput, onSendCustom, onUpload, onReplay, onStateChange }: {
  source: Source;
  webhookUrl: string;
  phase: 'build' | 'monitor';
  onOpenFull: () => void;
  onAddInput: () => void;
  onSendCustom: () => void;
  onUpload: () => void;
  onReplay: () => void;
  /** Notifies parent when status changes via the inline pause/resume
   *  button so the page can re-render dependent UI (ErrorBanner copy,
   *  flow-strip "PAUSED" overlay, etc.) without a full source refetch.
   *  UX Phase C: top-of-page pause/archive — was buried 2-3 clicks deep
   *  in Settings tab. */
  onStateChange?: (s: Source) => void;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [pending, setPending] = useState<'pause' | 'resume' | 'archive' | null>(null);
  async function togglePause() {
    if (pending) return;
    const next: 'active' | 'paused' = source.status === 'paused' ? 'active' : 'paused';
    setPending(next === 'paused' ? 'pause' : 'resume');
    try {
      const updated = await patchSource(source.source_id, { status: next });
      onStateChange?.(updated);
      toast.show(next === 'paused' ? 'Paused — ingestion stops, queued events held.' : 'Resumed — ingestion live.', { kind: 'success' });
    } catch (e) {
      toast.show(`Pause toggle failed · ${(e as Error).message}`, { kind: 'error' });
    } finally {
      setPending(null);
    }
  }
  async function archive() {
    if (pending) return;
    if (!window.confirm(`Archive "${source.name}"? Webhook returns 410 immediately; data preserved 90 days. Restore via Settings.`)) return;
    setPending('archive');
    try {
      await archiveSource(source.source_id);
      toast.show('Archived. Redirecting…', { kind: 'success' });
      setTimeout(() => navigate('/sources'), 800);
    } catch (e) {
      toast.show(`Archive failed · ${(e as Error).message}`, { kind: 'error' });
      setPending(null);
    }
  }
  const stateLabel = source.status === 'archived' ? 'ARCHIVED'
    : source.status === 'paused' ? 'PAUSED'
    : 'LIVE';
  const stateColor = source.status === 'archived' || source.status === 'paused'
    ? 'oklch(0.65 0.005 250)'
    : 'oklch(0.62 0.18 145)';
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      padding: '8px 12px', marginBottom: 16,
      background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8,
      fontSize: 12.5,
    }}>
      <span style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '2px 8px', fontFamily: 'var(--mono)', fontSize: 10,
        background: 'var(--bg-sunken)', borderRadius: 3,
        color: stateColor, textTransform: 'uppercase', letterSpacing: '0.06em',
      }}>
        <span style={{ width: 6, height: 6, borderRadius: '50%', background: stateColor }} />
        {stateLabel}
      </span>
      <span style={{ fontWeight: 500 }}>{source.name}</span>
      <code style={{
        flex: 1, minWidth: 200, padding: '4px 8px',
        fontFamily: 'var(--mono)', fontSize: 11.5, color: 'var(--ink-2)',
        background: 'var(--bg-sunken)', border: '1px solid var(--line)', borderRadius: 4,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{webhookUrl}</code>
      <button
        className="btn sm ghost"
        onClick={() => { void navigator.clipboard.writeText(webhookUrl); toast.show('URL copied.', { kind: 'success' }); }}
        title="Copy webhook URL"
      >copy</button>
      <button
        className="btn sm"
        onClick={async () => {
          try {
            const r = await fetch(webhookUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'test', amount: 42, ts: new Date().toISOString() }) });
            toast.show(r.ok ? `Test sent · ${r.status}` : `Test failed · ${r.status}`, { kind: r.ok ? 'success' : 'error' });
          } catch (e) {
            toast.show(`Test failed · ${(e as Error).message}`, { kind: 'error' });
          }
        }}
        title="POST a test event"
      >send test</button>
      {phase === 'monitor' && (
        <button className="btn sm ghost" onClick={onReplay} title="Replay events">replay</button>
      )}
      <button className="btn sm ghost" onClick={onAddInput} title="Add another input channel">+ input</button>
      <button className="btn sm ghost" onClick={onOpenFull} title="Show me how to integrate (curl, Node, Python)">how →</button>
      {/* Top-of-page state controls (UX Phase C #4): pause / resume /
          archive — was buried 2-3 clicks deep in Settings. Disabled
          when source is already archived (irreversible until restore). */}
      {source.status !== 'archived' && (
        <>
          <span aria-hidden="true" style={{ width: 1, height: 18, background: 'var(--line)', margin: '0 2px' }} />
          <button
            className="btn sm ghost"
            onClick={togglePause}
            disabled={pending !== null}
            title={source.status === 'paused' ? 'Resume — accept incoming events again' : 'Pause — webhook keeps responding 202 but nothing fires triggers / writes BQ'}
            style={{ color: source.status === 'paused' ? 'oklch(0.62 0.18 145)' : 'var(--ink-2)' }}
          >
            {pending === 'pause' ? 'pausing…' : pending === 'resume' ? 'resuming…' : source.status === 'paused' ? '▶ resume' : '⏸ pause'}
          </button>
          <button
            className="btn sm ghost"
            onClick={archive}
            disabled={pending !== null}
            title="Archive — webhook returns 410 immediately, data preserved 90 days"
            style={{ color: 'var(--danger)' }}
          >
            {pending === 'archive' ? 'archiving…' : 'archive'}
          </button>
        </>
      )}
    </div>
  );
}

// ── BuildPhaseCTA ─────────────────────────────────────────────────────────
// Prominent panel above the event stream during Phase 2. Operator just
// got their first events but has no triggers yet — guide them to the next
// decision (validate, enrich, or promote) with one-click templates that
// pre-fill the trigger dialog.
export function BuildPhaseCTA({ onPickValidate, onPickEnrich, onPickPromote }: {
  onPickValidate: () => void;
  onPickEnrich: () => void;
  onPickPromote: () => void;
}) {
  return (
    <div style={{
      marginBottom: 14, padding: 14,
      background: 'var(--accent-soft)', border: '1px solid var(--accent-line)', borderRadius: 10,
    }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 11, fontFamily: 'var(--mono)', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--ink-3)' }}>events arriving · what next?</div>
          <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--ink)', marginTop: 2 }}>Configure how this data is handled</div>
        </div>
        <div style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>Add a trigger — pick a starting point or build from scratch</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 8 }}>
        <button
          onClick={onPickValidate}
          style={{ textAlign: 'left', padding: 12, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, cursor: 'pointer', color: 'var(--ink)' }}
        >
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'oklch(0.55 0.14 60)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>validate</div>
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>Require fields</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.45 }}>Drop / quarantine events missing required fields. Stops bad data at the gate.</div>
        </button>
        <button
          onClick={onPickEnrich}
          style={{ textAlign: 'left', padding: 12, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, cursor: 'pointer', color: 'var(--ink)' }}
        >
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'oklch(0.50 0.14 250)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>enrich</div>
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>Fetch external profile</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.45 }}>Pull context from your API by id; merge response into the event payload.</div>
        </button>
        <button
          onClick={onPickPromote}
          style={{ textAlign: 'left', padding: 12, background: 'var(--bg-elev)', border: '1px solid var(--line)', borderRadius: 8, cursor: 'pointer', color: 'var(--ink)' }}
        >
          <div style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'oklch(0.45 0.12 145)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>promote</div>
          <div style={{ fontSize: 13, fontWeight: 500, marginTop: 4 }}>Stream into BigQuery</div>
          <div style={{ fontSize: 11.5, color: 'var(--ink-2)', marginTop: 2, lineHeight: 1.45 }}>Each event MERGEs into a typed BQ table. Schema auto-inferred; idempotent.</div>
        </button>
      </div>
    </div>
  );
}

// ── SourceHero (the big one) ──────────────────────────────────────────────
export function SourceHero({
  source, events, webhookUrl, howToOpen, onOpenHowTo, onCloseHowTo,
  onRename, onSendCustom, onUpload, onReplay, onAddInput,
}: {
  source: Source;
  events: DataHubEvent[];
  webhookUrl: string;
  howToOpen: boolean;
  onOpenHowTo: () => void;
  onCloseHowTo: () => void;
  onRename: () => void;
  onSendCustom: () => void;
  onUpload: () => void;
  onReplay: () => void;
  onAddInput: () => void;
}) {
  void onRename; // The rename popover is opened internally now; keep prop for compatibility.
  const toast = useToast();
  const navigate = useNavigate();
  const [showSlugPopover, setShowSlugPopover] = useState(false);
  const [showNamePopover, setShowNamePopover] = useState(false);

  // First-event landing pulse — when source.first_event_at transitions
  // from undefined to a value during this session, briefly highlight
  // the URL hero so the operator can see "your paste worked". Pure
  // visual feedback, dismissed after 4s.
  // BACKLOG: "First-event landing pulse".
  const [pulsing, setPulsing] = useState(false);
  const prevFirstEventAt = useRef<string | undefined>(source.first_event_at);
  useEffect(() => {
    if (!prevFirstEventAt.current && source.first_event_at) {
      setPulsing(true);
      const t = window.setTimeout(() => setPulsing(false), 4000);
      prevFirstEventAt.current = source.first_event_at;
      return () => window.clearTimeout(t);
    }
    prevFirstEventAt.current = source.first_event_at;
  }, [source.first_event_at]);

  // Map our backend model onto the design's vocabulary.
  // Design has workspace + streamId + streamAlias; we don't have multi-
  // workspace yet (tracked in BACKLOG.md), so the workspace slot is fixed.
  // streamId = the immutable short id (5 chars off `src_*`); name acts as
  // the optional alias.
  const workspace = 'workspace';
  const streamId = source.source_id.replace(/^src_/, '').slice(0, 5);
  const stream = source.name?.trim() || streamId;
  const isCustom = !!source.name?.trim();

  const archived = source.status === 'archived';
  const state: 'live' | 'paused' | 'waiting' = archived ? 'paused' : events.length === 0 ? 'waiting' : 'live';
  const stateLabelLong = (() => {
    if (archived) return 'ARCHIVED';
    if (events.length === 0) return 'WAITING';
    // Compute "Xs / Xm / Xh ago" from last event so operator at-a-glance
    // sees how active the source actually is.
    const lastIso = source.last_event_at ?? events[0]?.received_at;
    if (!lastIso) return `LIVE · ${events.length} event${events.length === 1 ? '' : 's'}`;
    const ms = Date.now() - new Date(lastIso).getTime();
    const ago = ms < 60_000 ? `${Math.max(1, Math.floor(ms / 1000))}s` :
      ms < 3_600_000 ? `${Math.floor(ms / 60_000)}m` :
      ms < 86_400_000 ? `${Math.floor(ms / 3_600_000)}h` :
      `${Math.floor(ms / 86_400_000)}d`;
    return `LIVE · ${ago} ago`;
  })();

  // First-run naming hint — yellow callout under the slug button when
  // the operator hasn't given the source a custom alias. Sticky dismiss
  // via localStorage so the hint stays quiet on subsequent visits.
  const HINT_KEY = 'mdh_naming_hint_dismissed';
  const [showNameHint, setShowNameHint] = useState(() => {
    if (isCustom) return false;
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(HINT_KEY) !== 'true';
  });
  useEffect(() => {
    if (isCustom && showNameHint) {
      setShowNameHint(false);
      try { localStorage.setItem(HINT_KEY, 'true'); } catch { /* private mode */ }
    }
  }, [isCustom, showNameHint]);
  const dismissNameHint = () => {
    setShowNameHint(false);
    try { localStorage.setItem(HINT_KEY, 'true'); } catch { /* private mode */ }
  };

  // Hint chips for the 3 most common alternative channels — only render
  // when the source has a single live input so they read as invitational
  // alternatives rather than competing with real tabs.
  const HINT_CHIPS: Array<{ kind: string; name: string; channel: 'push' | 'pull'; icon: React.ReactNode }> = [
    { kind: 'rest', name: 'REST API', channel: 'push',
      icon: <svg width="12" height="12" viewBox="0 0 13 13" fill="none"><rect x="2" y="3" width="9" height="7" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M4 5.5h5M4 7.5h3" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round"/></svg> },
    { kind: 'form', name: 'Public upload form', channel: 'push',
      icon: <svg width="12" height="12" viewBox="0 0 13 13" fill="none"><path d="M3 2.5h4.5L10 5v5.5a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V3.5a1 1 0 0 1 .5-1z" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg> },
    { kind: 'email', name: 'Email parse', channel: 'push',
      icon: <svg width="12" height="12" viewBox="0 0 13 13" fill="none"><rect x="2" y="3.5" width="9" height="6" rx="1" stroke="currentColor" strokeWidth="1.2"/><path d="M2.5 4.5l4 3 4-3" stroke="currentColor" strokeWidth="1.2" strokeLinejoin="round"/></svg> },
  ];

  return (
    <div className="inputs-card">
      {/* Source identity row */}
      <div className="src-identity">
        <span className="src-identity-eyebrow" title="The canonical identity of this source">
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
            <circle cx="5.5" cy="5.5" r="4" stroke="currentColor" strokeWidth="1.2"/>
            <circle cx="5.5" cy="5.5" r="1.5" fill="currentColor"/>
          </svg>
          Source
        </span>
        <span className="src-identity-path">
          <span className="src-identity-ws">{workspace}</span>
          <span className="src-identity-sep">/</span>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <button
              type="button"
              className="src-identity-slug"
              data-custom={isCustom || undefined}
              data-empty={!isCustom || undefined}
              onClick={() => { dismissNameHint(); setShowNamePopover(true); }}
              title={isCustom ? `Click to rename · short ID ${streamId} also works` : 'Click to give this source a memorable name'}
            >
              {isCustom ? stream : (
                <>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none" aria-hidden="true" className="src-identity-slug-cta-icon">
                    <path d="M5 1.5V8.5M1.5 5H8.5" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                  </svg>
                  <span className="src-identity-slug-cta-text">name this source</span>
                </>
              )}
            </button>
            {showNamePopover && (
              <SourceNameRenamePopover
                source={source}
                onClose={() => setShowNamePopover(false)}
                onSaved={(updated) => {
                  setShowNamePopover(false);
                  source.name = updated.name;
                  toast.show(`Source renamed to "${updated.name}".`, { kind: 'success' });
                }}
              />
            )}
          </span>
          {!isCustom && (
            <span
              className="src-identity-help"
              data-proactive={showNameHint || undefined}
              tabIndex={0}
              role="note"
              aria-label="Why name this source?"
            >
              <span className="src-identity-help-dot" aria-hidden="true">?</span>
              <span className="src-identity-help-tip" role="tooltip">
                <span className="src-identity-help-tip-arrow" aria-hidden="true" />
                Give it a memorable nickname — <code>pipedrive-prod</code> beats <code>{streamId}</code>. You can change this later.
                {showNameHint && (
                  <button
                    type="button"
                    className="src-identity-help-tip-dismiss"
                    onClick={(e) => { e.stopPropagation(); dismissNameHint(); }}
                    aria-label="Dismiss naming hint"
                    title="Dismiss"
                  >×</button>
                )}
              </span>
            </span>
          )}
        </span>
        <span className="src-identity-id" title="Immutable short ID — always resolves, even after rename">
          <span className="src-identity-id-lbl">ID</span>
          <code>{streamId}</code>
          <button
            type="button"
            className="src-identity-id-copy"
            onClick={() => navigator.clipboard.writeText(source.source_id)}
            title="Copy full source ID"
            aria-label="Copy source ID"
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none">
              <rect x="3" y="3" width="6" height="6" rx="1" stroke="currentColor" strokeWidth="1.1"/>
              <path d="M7.5 3V2a1 1 0 0 0-1-1h-3a1 1 0 0 0-1 1v3a1 1 0 0 0 1 1H4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/>
            </svg>
          </button>
        </span>
      </div>

      {/* Inputs rail — channel tab strip. Webhook is always rendered as
          the first active tab. Other channels become active tabs (instead
          of "or also accept" chips) when their config is enabled —
          today: form (form_config.enabled). api_pull/email handled by
          their own panels and do not yet auto-promote. */}
      {(() => {
        const formEnabled = !!source.form_config?.enabled;
        const activeChannelCount = 1 + (formEnabled ? 1 : 0);
        const hiddenHints = HINT_CHIPS.filter(c => !(c.kind === 'form' && formEnabled));
        return (
      <div className="inputs-rail">
        <div className="inputs-rail-label">
          <svg width="11" height="11" viewBox="0 0 11 11" fill="none" aria-hidden="true">
            <path d="M1 5.5h6m0 0L4.5 3M7 5.5L4.5 8" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
            <rect x="7.5" y="2" width="2.8" height="7" rx="0.4" stroke="currentColor" strokeWidth="1.2"/>
          </svg>
          <span>Inputs</span>
          <span className="inputs-rail-count">{activeChannelCount}</span>
        </div>
        <div className="inputs-rail-tabs" role="tablist">
          <button
            role="tab"
            aria-selected="true"
            className="inputs-rail-tab"
            data-active=""
            data-state={state}
            data-channel="push"
            title="Inbound webhook · the channel that's currently live"
          >
            <span className="inputs-rail-tab-icon">
              <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                <path d="M2 6.5h7M7 4l2.5 2.5L7 9" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span>
            <span className="inputs-rail-tab-name">Inbound webhook</span>
            <span className="inputs-rail-tab-state" data-state={state}>
              {state === 'live' ? 'live' : state === 'paused' ? 'paused' : 'waiting'}
            </span>
          </button>
          {formEnabled && (
            <button
              role="tab"
              className="inputs-rail-tab"
              data-active=""
              data-state={state}
              data-channel="push"
              onClick={() => navigate(`/sources/${source.source_id}?tab=settings`)}
              title="Public upload form is enabled — click to manage schema"
            >
              <span className="inputs-rail-tab-icon">
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none" aria-hidden="true">
                  <path d="M3 2.5h4.5L10 5v5.5a1 1 0 0 1-1 1H3.5a1 1 0 0 1-1-1V3.5a1 1 0 0 1 .5-1z" stroke="currentColor" strokeWidth="1.3" strokeLinejoin="round"/>
                </svg>
              </span>
              <span className="inputs-rail-tab-name">Public upload form</span>
              <span className="inputs-rail-tab-state" data-state="live">live</span>
            </button>
          )}
          <div className="inputs-rail-hint" role="presentation">
            <span className="inputs-rail-hint-or">or also accept</span>
            {hiddenHints.map(c => (
              <button
                key={c.kind}
                type="button"
                className="inputs-rail-hint-chip"
                data-channel={c.channel}
                onClick={onAddInput}
                title={`Open the input picker — pick ${c.name} or another channel`}
              >
                <span className="inputs-rail-hint-chip-icon">{c.icon}</span>
                <span className="inputs-rail-hint-chip-name">{c.name}</span>
              </button>
            ))}
            <button
              type="button"
              className="inputs-rail-hint-more"
              onClick={onAddInput}
              title="Browse all available input channels"
            >more…</button>
          </div>
        </div>
        <div className="inputs-rail-add-wrap">
          <button
            className="inputs-rail-add"
            onClick={onAddInput}
            title="Browse the 4 alternative input channels"
          >
            <svg width="11" height="11" viewBox="0 0 11 11" fill="none"><path d="M5.5 2v7M2 5.5h7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/></svg>
            Add input
          </button>
        </div>
      </div>
        );
      })()}

      {/* URL hero — webhook private address + actions + blurb */}
      <div className="url-hero" data-first-event-pulse={pulsing || undefined}>
        <div className="url-hero-eyebrow">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <svg width="11" height="11" viewBox="0 0 13 13" fill="none" aria-hidden="true">
              <path d="M3.5 6V4.5a3 3 0 1 1 6 0V6" stroke="currentColor" strokeWidth="1.3" />
              <rect x="2.3" y="6" width="8.4" height="5.5" rx="1" stroke="currentColor" strokeWidth="1.3" />
              <circle cx="6.5" cy="8.7" r="0.7" fill="currentColor" />
            </svg>
            Your private address · point any webhook here
          </span>
          <span
            className="pill-live-split"
            data-paused={(source.status === 'paused' || archived) || undefined}
            data-waiting={events.length === 0 && !archived && source.status !== 'paused' || undefined}
          >
            <span className="pls-state"><i />{archived ? 'ARCHIVED' : source.status === 'paused' ? 'PAUSED' : stateLabelLong}</span>
            {!archived && (
              <button
                type="button"
                className="pls-action"
                onClick={async () => {
                  const next = source.status === 'paused' ? 'active' : 'paused';
                  try {
                    await patchSource(source.source_id, { status: next });
                    toast.show(
                      next === 'paused'
                        ? 'Ingestion paused · partners get 503 Retry-After. Webhook URL stays valid.'
                        : 'Ingestion resumed · webhook accepts events again.',
                      { kind: 'success' },
                    );
                    // Optimistic local flip; parent refetches on next mount.
                    source.status = next;
                  } catch (e) {
                    toast.show(`${next === 'paused' ? 'Pause' : 'Resume'} failed: ${(e as Error).message}`, { kind: 'error' });
                  }
                }}
                title={source.status === 'paused' ? 'Resume — accept events again' : 'Pause — webhook returns 503 (URL stays valid). Use during incidents.'}
              >{source.status === 'paused' ? '▶ resume' : '‖ pause'}</button>
            )}
          </span>
        </div>
        <div className="big-url">
          <span className="url-text">
            {(() => {
              const m = webhookUrl.match(/^(https?:\/\/)([^/]+)(\/.+\/)([^/]+)$/);
              if (!m) return <span>{webhookUrl}</span>;
              const [, proto, host, prefix, slug] = m;
              return (
                <>
                  <span className="muted">{proto}</span>{host}<span className="muted">{prefix}</span>
                  <span className="url-slug" style={{ position: 'relative' }}>
                    <button
                      type="button"
                      className="url-slug-text"
                      onClick={() => setShowSlugPopover(true)}
                      title={source.slug_alias ? `Custom slug · ${source.source_id} also resolves` : 'Click to set a friendly slug'}
                      style={{ background: 'transparent', border: 'none', padding: 0, font: 'inherit', color: 'inherit', cursor: 'pointer' }}
                    >{slug}</button>
                    {showSlugPopover && (
                      <SlugRenamePopover
                        source={source}
                        onClose={() => setShowSlugPopover(false)}
                        onSaved={(updated) => {
                          setShowSlugPopover(false);
                          // Optimistically reflect new slug in webhookUrl rendering;
                          // the parent listens via getSource on next mount/poll.
                          source.slug_alias = updated.slug_alias ?? undefined;
                          source.slug_history = updated.slug_history;
                        }}
                      />
                    )}
                  </span>
                </>
              );
            })()}
          </span>
          <div className="url-actions">
            <button
              className="btn sm"
              onClick={() => {
                void navigator.clipboard.writeText(webhookUrl);
                toast.show('URL copied to clipboard.', { kind: 'success' });
              }}
              title="Copy URL to clipboard"
            >copy</button>
            <button
              className="btn sm primary"
              onClick={async () => {
                const start = Date.now();
                try {
                  const r = await fetch(webhookUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event: 'test', amount: 42, ts: new Date().toISOString() }) });
                  const ms = Date.now() - start;
                  if (r.ok) {
                    toast.show(`Test event sent · ${r.status} in ${ms}ms · check Live feed.`, { kind: 'success' });
                  } else {
                    const detail = await r.text().catch(() => '');
                    toast.show(`Test rejected · ${r.status} ${r.statusText} ${detail.slice(0, 120)}`, { kind: 'error' });
                  }
                } catch (e) {
                  toast.show(`Test failed · ${(e as Error).message}`, { kind: 'error' });
                }
              }}
              title="POST a test event right now"
            >send test</button>
            <UrlHeroOverflow
              webhookUrl={webhookUrl}
              portalUrl={source.portal_url}
              onSendCustom={onSendCustom}
              onUpload={onUpload}
              onReplay={onReplay}
            />
          </div>
        </div>
        <p className="url-blurb">
          <strong style={{ fontWeight: 600, color: 'var(--ink)' }}>Send anything you want.</strong>{' '}
          JSON, XML, CSV, a file upload, a plain POST — we accept it all and figure out the rest. Point this URL at any tool, any language, any format.{' '}
          <button
            type="button"
            className="url-blurb-link"
            onClick={onOpenHowTo}
          >First time? Show me how →</button>
        </p>
        {howToOpen && <HowToSend webhookUrl={webhookUrl} onClose={onCloseHowTo} />}
      </div>
    </div>
  );
}

// ── UrlHeroOverflow ───────────────────────────────────────────────────────
// Collapses 4–5 secondary URL-hero actions into a "..." menu. Primary
// actions (copy URL + send test) stay inline; rare/destructive ones (send
// custom, upload, replay, copy curl, share portal) live behind a single
// affordance to drop visual noise from 7 buttons → 3.
function UrlHeroOverflow({ webhookUrl, portalUrl, onSendCustom, onUpload, onReplay }: {
  webhookUrl: string;
  portalUrl?: string;
  onSendCustom: () => void;
  onUpload: () => void;
  onReplay: () => void;
}): React.ReactElement {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent): void {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void { if (e.key === 'Escape') setOpen(false); }
    document.addEventListener('click', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('click', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const items: Array<{ label: string; onClick: () => void; title?: string }> = [
    { label: 'send custom event…', onClick: () => { setOpen(false); onSendCustom(); }, title: 'POST a custom event with editable JSON' },
    { label: 'upload file…', onClick: () => { setOpen(false); onUpload(); }, title: 'Bulk JSON / CSV / ZIP / XLSX upload' },
    { label: 'replay…', onClick: () => { setOpen(false); onReplay(); }, title: 'Re-process stored events through current triggers' },
    { label: 'copy curl command', onClick: () => { setOpen(false); navigator.clipboard.writeText(`curl -X POST '${webhookUrl}' -H 'Content-Type: application/json' -d '{"event":"hello","amount":42}'`); }, title: 'Ready-to-run terminal command' },
  ];
  if (portalUrl) items.push({ label: 'copy partner portal link', onClick: () => { setOpen(false); navigator.clipboard.writeText(portalUrl); }, title: 'Public portal share — give to the data sender' });

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block', marginLeft: 6 }}>
      <button className="btn sm" onClick={() => setOpen(o => !o)} title={`${items.length} more actions`} aria-haspopup="menu" aria-expanded={open}>
        more
      </button>
      {open && (
        <div role="menu" style={{
          position: 'absolute', top: 'calc(100% + 4px)', right: 0,
          minWidth: 240, background: 'var(--bg-elev)',
          border: '1px solid var(--line)', borderRadius: 6,
          padding: 4, zIndex: 100,
          boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
        }}>
          {items.map((it, i) => (
            <button
              key={i}
              role="menuitem"
              onClick={it.onClick}
              title={it.title}
              style={{
                display: 'block', width: '100%', textAlign: 'left',
                padding: '8px 12px', fontSize: 13, fontFamily: 'var(--ui)',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--ink)', borderRadius: 4,
              }}
              onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-sunken)')}
              onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
            >{it.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
